import { useMemo } from 'react'
import { TriangleAlert, type LucideIcon } from 'lucide-react'
import classNames from 'classnames'

import Flex from '@/elements/Flex'

import './RadioGroup.scss'

interface RadioGroupOption {
  value: string | boolean
  text?: string
  Icon?: LucideIcon
}

interface RadioGroupProps {
  value?: string | boolean
  name: string
  options?: RadioGroupOption[]
  onChange: (value: string | boolean) => void
  yesOrNo?: boolean
  fullWidth?: boolean
  disabled?: boolean
  error?: string
}

function RadioGroup({
  value,
  name,
  options,
  onChange,
  yesOrNo,
  fullWidth,
  disabled,
  error
}: RadioGroupProps) {
  const internalOptions = useMemo(() => {
    if (yesOrNo) {
      return [
        { value: true, text: 'Yes' },
        { value: false, text: 'No' },
      ]
    }
    return options || []
  }, [yesOrNo, options])

  return (
    <Flex className='form-field' wfull={fullWidth}>
      <Flex inline className='radio-group' wfull={fullWidth}>
        {internalOptions.map(({ value: optionValue, text, Icon }) => (
          <label
            key={String(optionValue)}
            className={classNames('radio-option', { selected: value === optionValue })}
            style={{ width: fullWidth ? '100%' : '' }}
          >
            <input
              type='radio'
              name={name}
              value={String(optionValue)}
              checked={value === optionValue}
              onChange={() => onChange(optionValue)}
              disabled={disabled}
            />
            {text}
            {Icon && <Icon />}
          </label>
        ))}
      </Flex>
      {error && (
        <span className='field-error'>
          <TriangleAlert size={12} />
          {error}
        </span>
      )}
    </Flex>
  )
}

export default RadioGroup
